import { api } from './api';
import {
  Estimate,
  CreateEstimateData,
  UpdateEstimateData,
  ConvertToInvoiceData,
  EstimateStats,
  EstimatesResponse,
  EstimateFilters
} from '../types/estimate';

export const estimateService = {
  async getEstimates(filters: EstimateFilters = {}): Promise<EstimatesResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.jobId) params.append('jobId', filters.jobId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/estimates?${params.toString()}`);
    return response.data;
  },

  async getEstimate(id: string): Promise<Estimate> {
    const response = await api.get(`/estimates/${id}`);
    return response.data;
  },

  async createEstimate(data: CreateEstimateData): Promise<Estimate> {
    const response = await api.post('/estimates', data);
    return response.data;
  },

  async updateEstimate(id: string, data: UpdateEstimateData): Promise<Estimate> {
    const response = await api.put(`/estimates/${id}`, data);
    return response.data;
  },

  async deleteEstimate(id: string): Promise<void> {
    const response = await api.delete(`/estimates/${id}`);
    return response.data;
  },

  async getEstimateStats(): Promise<EstimateStats> {
    const response = await api.get('/estimates/stats');
    return response.data;
  },

  async markEstimateAsSent(id: string): Promise<Estimate> {
    const response = await api.patch(`/estimates/${id}/send`);
    return response.data;
  },

  async acceptEstimate(id: string): Promise<Estimate> {
    const response = await api.patch(`/estimates/${id}/accept`);
    return response.data;
  },

  async declineEstimate(id: string): Promise<Estimate> {
    const response = await api.patch(`/estimates/${id}/decline`);
    return response.data;
  },

  async convertToInvoice(id: string, data: ConvertToInvoiceData): Promise<{ invoice: any; estimate: Estimate }> {
    const response = await api.post(`/estimates/${id}/convert-to-invoice`, data);
    return response.data;
  },

  // Helper function to calculate line totals
  calculateLineTotal(quantity: number, rate: number, taxRate: number = 0): { amount: number; taxAmount: number; total: number } {
    const amount = quantity * rate;
    const taxAmount = (amount * taxRate) / 100;
    const total = amount + taxAmount;

    return { amount, taxAmount, total };
  },

  // Helper function to calculate estimate totals
  calculateEstimateTotals(items: { quantity: number; rate: number; taxRate?: number }[]): { subtotal: number; taxAmount: number; total: number } {
    let subtotal = 0;
    let taxAmount = 0;

    items.forEach(item => {
      const lineTotal = this.calculateLineTotal(item.quantity, item.rate, item.taxRate || 0);
      subtotal += lineTotal.amount;
      taxAmount += lineTotal.taxAmount;
    });

    return { subtotal, taxAmount, total: subtotal + taxAmount };
  },

  // Helper function to format estimate number
  formatEstimateNumber(number: string): string {
    return number.startsWith('EST') ? number : `EST${number}`;
  },

  // Helper function to check if estimate can be edited
  canEditEstimate(status: string): boolean {
    return status === 'DRAFT';
  },

  // Helper function to check if estimate can be deleted
  canDeleteEstimate(status: string): boolean {
    return status === 'DRAFT';
  },

  // Helper function to check if estimate is expired
  isEstimateExpired(validUntil: string): boolean {
    return new Date(validUntil) < new Date();
  },

  // Helper function to check if estimate can be converted to invoice
  canConvertToInvoice(estimate: Estimate): boolean {
    return estimate.status === 'ACCEPTED' &&
           !estimate.convertedToInvoiceId &&
           !this.isEstimateExpired(estimate.validUntil);
  },

  // Helper function to get days until expiry
  getDaysUntilExpiry(validUntil: string): number {
    const today = new Date();
    const expiryDate = new Date(validUntil);
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  // Helper function to get available status transitions
  getAvailableStatusTransitions(currentStatus: string): string[] {
    switch (currentStatus) {
      case 'DRAFT':
        return ['SENT'];
      case 'SENT':
        return ['ACCEPTED', 'DECLINED'];
      case 'ACCEPTED':
        return []; // Can only convert to invoice
      case 'DECLINED':
      case 'EXPIRED':
        return []; // Terminal states
      default:
        return [];
    }
  },

  // Helper function to get expiry warning
  getExpiryWarning(validUntil: string): string | null {
    const daysUntilExpiry = this.getDaysUntilExpiry(validUntil);

    if (daysUntilExpiry < 0) {
      return 'This estimate has expired';
    } else if (daysUntilExpiry === 0) {
      return 'This estimate expires today';
    } else if (daysUntilExpiry <= 3) {
      return `This estimate expires in ${daysUntilExpiry} day${daysUntilExpiry > 1 ? 's' : ''}`;
    }

    return null;
  }
};
